import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../SupabaseClient.tsx";
import "./TaxCenter.css";
import { PiDownloadSimple, PiFileCsv, PiFileZip, PiMagnifyingGlass, PiMagnifyingGlassPlus, PiPlus } from "react-icons/pi";
import ActionButton from "../components/ActionButton.tsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";

interface Reimbursement {
  id: number;
  order_id: number;
  orders: { name: string; beneficiary: string; storage_path: string; original_filename: string }[];
  reimburse_date: string;
  reimburse_amount: number;
}

const TaxCenter: React.FC = () => {
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchYears = async () => {
      setLoading(true);
      setError("");
      const { data, error } = await supabase
        .from("order_reimburse")
        .select("reimburse_date")
        .order("reimburse_date", { ascending: false });
      if (error) {
        setError("Failed to fetch years.");
        setLoading(false);
        return;
      }
      const yearSet = new Set<number>();
      (data || []).forEach((r: any) => {
        if (r.reimburse_date) {
          yearSet.add(new Date(r.reimburse_date).getFullYear());
        }
      });
      const yearArr = Array.from(yearSet).sort((a, b) => b - a);
      setYears(yearArr);
      setSelectedYear(yearArr.length > 0 ? yearArr[0] : null);
      setLoading(false);
    };
    fetchYears();
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    setError("");
    const fetchReimbursements = async () => {
      const { data, error } = await supabase
        .from("order_reimburse")
        .select("id, order_id, reimburse_date, reimburse_amount, orders(name, beneficiary, storage_path, original_filename)")
        .gte("reimburse_date", `${selectedYear}-01-01`)
        .lte("reimburse_date", `${selectedYear}-12-31`)
        .order("reimburse_date", { ascending: true });
      if (error) {
        setError("Failed to fetch reimbursements.");
        setLoading(false);
        return;
      }
      setReimbursements(data || []);
      setLoading(false);
    };
    fetchReimbursements();
  }, [selectedYear]);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(Number(e.target.value));
  };

  const handleExportZip = async () => {
    setDownloading(true);
    const zip = new JSZip();

    // Fetch each file from Supabase storage and add to zip
    for (const order of reimbursements) {
      console.log(order);
      if (order.orders["storage_path"]) {
        // Download file from Supabase storage
        const { data, error } = await supabase.storage
          .from("order_receipts")
          .download(order.orders["storage_path"]);

        if (!error && data) {
          // Read file as ArrayBuffer
          const fileBlob = await data.arrayBuffer();
          const extension = order.orders["storage_path"].split('.').pop() || ''
          zip.file(order.orders["original_filename"] || order.order_id + '.' + extension, fileBlob);
        }
      }
    }

    // Generate zip and trigger download
    zip
      .generateAsync({ type: "blob" })
      .then((content) => {
        saveAs(content, "receipts_" + selectedYear + ".zip");
        setDownloading(false);
      })
      .catch(() => {
        setDownloading(false);
      });
  };

  const handleDownloadCsv = async () => {
    setDownloading(true);
    setError("");
    try {
      // No receipt_url, so just download a CSV summary
      const csvRows = [
        "Date Reimbursed,Description,Beneficiary,Reimbursed Amount",
      ];
      reimbursements.forEach(r => {
        csvRows.push(`${new Date(r.reimburse_date).toLocaleDateString()},${r.orders["name"] || "N/A"},${r.orders["beneficiary"] || "N/A"},${r.reimburse_amount.toFixed(2)}`);
      });
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reimbursements_${selectedYear}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to download receipts. Please try again.");
    }
    setDownloading(false);
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Tax Center</h1>
            <p>
              Review all of your HSA reimbursements for a given year. Download a zipped bundle of all reimbursed receipts and supporting documents for your records or tax filing.
            </p>
          </div>
        </div>
        {!loading && (
          <>
            <div className="taxcenter-container">
              {years.length !== 0 ? (
                <>
                  <div className="taxcenter-controls">
                    <div className="taxcenter-conrols-actions">
                      <label htmlFor="year-select">Year:</label>
                      <select
                        id="year-select"
                        value={selectedYear || ""}
                        onChange={handleYearChange}
                        disabled={loading || years.length === 0}
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <div className="taxcenter-conrols-buttons">
                      <ActionButton
                        icon={<PiFileZip />}
                        text={downloading ? "Downloading..." : "Download Receipts"}
                        onClick={handleExportZip}
                        disabled={downloading || reimbursements.length === 0}
                        responsive={true}
                        secondary={true}
                      />
                      <ActionButton
                        icon={<PiFileCsv />}
                        text={downloading ? "Downloading..." : "Download Summary"}
                        onClick={handleDownloadCsv}
                        disabled={downloading || reimbursements.length === 0}
                        responsive={true}
                      />
                    </div>
                  </div>
                  {error && <div className="error-message">{error}</div>}
                </>
              ) : (
                <div className="container-empty">
                  <p>
                    You haven't reimbursed any eligible expenses yet. Tax information will be available once you start logging expenses.
                  </p>
                  <ActionButton
                    onClick={() => navigate("/reimburse")}
                    text="Reimburse Now"
                    icon={<PiPlus />}
                  />
                </div>
              )}
            </div>
            {reimbursements.length > 0 && (
              <div className="receipt-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date Reimbursed</th>
                      <th>Description</th>
                      <th>Beneficiary</th>
                      <th>Reimbursed Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reimbursements.map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.reimburse_date).toLocaleDateString()}</td>
                        <td>{r.orders ? r.orders["name"] : "N/A"}</td>
                        <td>{r.orders ? r.orders["beneficiary"] : "N/A"}</td>
                        <td>${r.reimburse_amount.toFixed(2)}</td>
                        <td>
                          <ActionButton
                            text="View"
                            onClick={() => navigate(`/receipt-library/${r.order_id}`)}
                            secondary={true}
                            responsive={true}
                            icon={<PiMagnifyingGlassPlus />}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaxCenter;